import { Component, inject, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { AuthService } from 'src/app/auth/services';
import { RestService } from 'src/app/services/rest.service';
import { Snackbar, TranslationService } from 'src/app/services';
import { UserService } from 'src/app/user/services';
import { LoginResponseError } from 'src/app/auth/interfaces';
import { SocialLoginButtonsComponent } from 'src/app/auth/components/social-login-buttons/social-login-buttons.component';
import { OfficeAccessLinkComponent } from '../office-access-link/office-access-link.component';

@Component({
  selector: 'app-login-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, SocialLoginButtonsComponent, OfficeAccessLinkComponent],
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoginDialogComponent {
  private _authService = inject(AuthService);
  private _dialogRef = inject(MatDialogRef<LoginDialogComponent>);
  private _rest = inject(RestService);
  private _snackbar = inject(Snackbar);
  private _translationService = inject(TranslationService);
  private _userService = inject(UserService);

  passwordType: string = 'password';

  /** HRS-3134: el enlace de acceso para quien se registró en una oficina de turismo. */
  protected loginFailed = this._authService.loginFailed;
  protected snackbarContainer = document.body;

  public loginForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required, Validators.minLength(6)]),
    remember: new FormControl(false),
  });

  protected login(): void {
    const email = this.loginForm.get('email')?.value;
    const password = this.loginForm.get('password')?.value;

    this._authService.loginAndSetUser(email, password).subscribe({
      next: () => {
        this._snackbar.showSuccessSnackbar(
          this._translationService.instant('Inicio de sesión exitoso'),
          undefined,
          document.body,
        );
        this._dialogRef.close();
      },
      error: (error: LoginResponseError) => {
        this._snackbar.showErrorSnackbar(error.message, undefined, document.body);
      },
    });
  }

  protected dismissDialog(): void {
    this._dialogRef.close();
  }

  protected showPassword(): void {
    this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
  }

  protected getIconStatus(): string {
    return this.passwordType === 'password' ? 'eye-off' : 'eye';
  }

  protected register(): void {
    this._dialogRef.close();
    this._userService.registerInMatDialog();
  }

  protected recoverPassword(): void {
    // HRS-3187: sin el idioma, el CMS renderiza la página en español.
    const url = `${this._rest.apiUrlBase}/api/forgot-password?_locale=${this._translationService.selectedLanguage()}`;
    window.open(url, '_blank');
  }

  protected focusin(evt: Event): void {
    evt.stopPropagation();
  }
}
